<template>
  <div class="login-page">
    <div class="login-page__hero">
      <div class="login-page__lang">
        <LocaleProvider>
          <template slot-scope="{ changeLocale, currentLocale }">
            <LocaleToggle :changeLocale="changeLocale" :currentLocale="currentLocale" />
          </template>
        </LocaleProvider>
      </div>

      <div class="login-page__hero-body">
        <a class="login-page__logo-link" :href="websiteLink" aria-label="Aloha">
          <AlohaLogo class="login-page__logo" />
        </a>
        <h2 class="login-page__headline">{{ $t('login.tagline') }}</h2>
        <p class="login-page__subhead">{{ $t('login.subtagline') }}</p>

        <ul class="login-page__points">
          <li v-for="point in points" :key="point" class="login-page__point">
            <BaseIcon name="check" class="login-page__point-icon" aria-hidden="true" />
            <span>{{ point }}</span>
          </li>
        </ul>
      </div>

      <p class="login-page__canadian">
        <FleurDeLys class="login-page__canadian-icon" aria-hidden="true" />
        {{ $t('login.canadian-product') }}
      </p>
    </div>

    <div class="login-page__form-column">
      <div class="login-page__form-wrapper">
        <LoginForm />
        <div class="login-page__cta-banner">
          <span class="login-page__cta-text">{{ $t('login.no-account') }}</span>
          <BaseButton is-full-width is-large is-grey hasIconLeft @click.native="openContact">
            <BaseIcon name="rocket-launch" aria-hidden="true" />
            {{ $t('login.cta') }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AlohaLogo from '@/assets/images/aloha-logo.svg';
import FleurDeLys from '@/assets/images/fleur-de-lys.svg';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon';
import LocaleProvider from '@/components/locale/LocaleProvider';
import LocaleToggle from '@/components/locale/LocaleToggle';
import LoginForm from '@/components/page-login/LoginForm.vue';
import { Config } from '@/config';
import { LOCALE } from '@/util/i18n/locale';

export default {
  name: 'LoginPage',
  components: {
    AlohaLogo,
    FleurDeLys,
    BaseButton,
    BaseIcon,
    LocaleProvider,
    LocaleToggle,
    LoginForm,
  },
  computed: {
    websiteLink() {
      return this.$i18n.locale === LOCALE.EN_CA ? Config.website.en : Config.website.fr;
    },
    points() {
      return [this.$t('login.points.daily'), this.$t('login.points.total'), this.$t('login.points.platforms')];
    },
  },
  methods: {
    openContact() {
      this.$track.contactUs('login_cta');
      window.open(Config.contact, '_blank', 'noopener,noreferrer');
    },
  },
};
</script>

<style lang="scss">
.login-page {
  background-color: $color-surface-raised;
  display: flex;
  height: var(--viewport-height);
  width: 100%;

  &__hero {
    background-image: linear-gradient(rgba($color-accent-600, 0.5), rgba($color-brand-700, 0.8)),
      url('../assets/images/login-bg-gradient.avif');
    background-position: center;
    background-size: cover;
    color: $color-text-inverse;
    display: flex;
    flex: 0 0 50%;
    flex-direction: column;
    height: 100%;
    padding: $space-3xl;
  }

  &__lang {
    display: flex;
    justify-content: flex-end;

    .locale-toggle {
      color: $color-text-inverse;
    }
  }

  &__hero-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    max-width: 35rem;
  }

  &__logo-link {
    align-self: flex-start;
    border-radius: $radius-sm;
    color: $color-text-inverse;
    display: inline-flex;
    margin-bottom: $space-xl;

    // Keep the currentColor logo white; don't inherit the global link colour.
    &:hover,
    &:focus,
    &:active {
      color: $color-text-inverse;
    }

    &:focus-visible {
      outline: 2px solid $color-text-inverse;
      outline-offset: 4px;
    }
  }

  &__logo {
    height: auto;
    width: 168px;
  }

  &__headline {
    color: $color-text-inverse;
    font-family: $font-family-heading;
    font-size: 3rem;
    margin: 0 0 $space-sm 0;
    white-space: pre-line;
  }

  &__subhead {
    color: $color-text-inverse;
    font-family: $font-family-body;
    font-size: 1rem;
    margin: 0 0 $space-xl 0;
  }

  &__points {
    display: flex;
    flex-direction: column;
    gap: $space-md;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__point {
    align-items: flex-start;
    color: $color-text-inverse;
    display: flex;
    font-family: $font-family-body;
    @include text-style('body-lg');
    gap: $space-sm;
  }

  &__point-icon {
    flex-shrink: 0;
    height: 22px;
    margin-top: 2px;
    width: 22px;
  }

  &__canadian {
    align-items: center;
    color: $color-text-inverse;
    display: inline-flex;
    font-family: $font-family-body;
    gap: $space-1;
    @include text-style('label-md');
    margin: 0;
    opacity: 0.85;
  }

  &__canadian-icon {
    height: 12px;
    width: auto;
  }

  &__form-column {
    align-items: center;
    display: flex;
    flex: 0 0 50%;
    justify-content: center;
    overflow: auto;
    padding: $space-xl;
  }

  &__form-wrapper {
    display: flex;
    flex-direction: column;
    gap: $space-lg;
    max-width: 440px;
    width: 100%;
  }

  &__cta-banner {
    align-items: center;
    // Near-white tint so the grey CTA button still reads against the banner.
    background-color: $color-accent-50;
    border: 1px solid $color-accent-200;
    border-radius: $radius-md;
    display: flex;
    flex-direction: column;
    gap: $space-sm;
    padding: $space-md;
    text-align: center;
  }

  &__cta-text {
    color: $color-text-body;
    @include text-style('body-md');
  }

  // The CTA banner replaces the form's own footer here.
  .login-form__footer {
    display: none;
  }
}

@media screen and (max-width: 1080px) {
  .login-page {
    flex-direction: column;
    height: auto;
    min-height: var(--viewport-height);

    &__hero {
      flex: 0 0 auto;
      padding: $space-md;
    }

    &__points,
    &__subhead {
      display: none;
    }

    &__logo-link {
      margin-bottom: $space-sm;
    }

    &__logo {
      width: 132px;
    }

    &__headline {
      @include text-style('heading-xl');
      margin-bottom: 0;
    }

    &__form-column {
      flex: 1 1 auto;
    }
  }
}
</style>
